import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import {
  Calendar,
  Mail,
  Phone as PhoneIcon,
  ExternalLink,
  Check,
  X,
  Clock,
  RotateCw,
} from "lucide-react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

const STATUS_TINT = {
  confirmed: "bg-[#FFB000]/10 border-[#FFB000]/30 text-[#FFB000]",
  completed: "bg-emerald-500/10 border-emerald-500/30 text-emerald-300",
  no_show: "bg-rose-500/10 border-rose-500/30 text-rose-300",
  cancelled: "bg-white/5 border-white/10 text-white/40",
};

const STATUS_OPTIONS = [
  { value: "confirmed", label: "Confirmed" },
  { value: "completed", label: "Completed" },
  { value: "no_show", label: "No-show" },
  { value: "cancelled", label: "Cancelled" },
];

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await api.get("/appointments", { params });
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const setStatus = async (id, newStatus) => {
    try {
      await api.patch(`/appointments/${id}`, { status: newStatus });
      toast.success(`Marked ${newStatus.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#FFB000] font-medium mb-2">Pipeline</div>
          <h1 className="text-3xl font-bold tracking-tight">Appointments</h1>
          <p className="text-sm text-white/50 mt-2">All bookings — chatbot, Hear Ashley, and outbound calls.</p>
        </div>
        <button
          onClick={load}
          data-testid="appointments-refresh"
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-white/60 hover:text-white border border-white/10 hover:border-white/25 transition-colors"
        >
          <RotateCw className="w-3.5 h-3.5" />
          Refresh
        </button>
      </div>

      {/* Filter row */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: "all", l: "All" },
          { v: "confirmed", l: "Confirmed" },
          { v: "completed", l: "Completed" },
          { v: "no_show", l: "No-show" },
          { v: "cancelled", l: "Cancelled" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`appointments-filter-${f.v}`}
            onClick={() => setFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              filter === f.v
                ? "bg-[#FFB000]/15 border-[#FFB000]/40 text-[#FFB000]"
                : "bg-transparent border-white/10 text-white/55 hover:text-white hover:border-white/25"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/50 text-sm py-12">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-[#121214] border border-white/5 rounded-2xl p-12 text-center" data-testid="appointments-empty">
          <Calendar className="w-10 h-10 text-white/20 mx-auto mb-4" strokeWidth={1.5} />
          <div className="text-white/70 font-medium">No appointments yet</div>
          <div className="text-white/40 text-sm mt-1">Bookings from the website chatbot and Ashley demos will appear here.</div>
        </div>
      ) : (
        <div className="grid gap-3">
          {items.map((a) => {
            const dt = a.scheduled_at ? parseISO(a.scheduled_at) : null;
            const tint = STATUS_TINT[a.status] || STATUS_TINT.confirmed;
            return (
              <div
                key={a.id}
                data-testid={`appointment-row-${a.id}`}
                className="bg-[#121214] border border-white/5 rounded-2xl p-5 grid grid-cols-12 gap-4 items-center hover:border-white/15 transition-colors"
              >
                <div className="col-span-12 md:col-span-3">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-white/40 font-medium">{a.source || "—"}</div>
                  <div className="text-white text-sm font-semibold mt-1">{a.name}</div>
                  <div className="text-white/45 text-xs mt-0.5">
                    {dt && formatDistanceToNow(dt, { addSuffix: true })}
                  </div>
                </div>

                <div className="col-span-12 md:col-span-4 space-y-1.5">
                  <div className="flex items-center gap-2 text-sm text-[#FFB000] font-medium">
                    <Clock className="w-3.5 h-3.5" />
                    {dt ? format(dt, "EEE, MMM d · h:mm a") : "—"}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-white/55">
                    <Mail className="w-3 h-3" />
                    <a href={`mailto:${a.email}`} className="hover:text-[#FFB000] truncate">{a.email}</a>
                  </div>
                  {a.phone && (
                    <div className="flex items-center gap-2 text-xs text-white/55">
                      <PhoneIcon className="w-3 h-3" />
                      <a href={`tel:${a.phone}`} className="hover:text-[#FFB000]">{a.phone}</a>
                    </div>
                  )}
                </div>

                <div className="col-span-12 md:col-span-3">
                  {a.notes && (
                    <div className="text-[12px] text-white/55 line-clamp-3 leading-relaxed">{a.notes}</div>
                  )}
                  {a.calendar_event_link && (
                    <a
                      href={a.calendar_event_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] text-[#FFB000] hover:underline mt-1"
                    >
                      <ExternalLink className="w-3 h-3" /> Open in Google
                    </a>
                  )}
                  {a.reminder_sent_at && (
                    <div className="inline-flex items-center gap-1 text-[10px] text-sky-400/80 mt-1.5 ml-2 px-2 py-0.5 bg-sky-500/10 border border-sky-500/20 rounded">
                      <Check className="w-2.5 h-2.5" /> SMS reminder sent
                    </div>
                  )}
                </div>

                <div className="col-span-12 md:col-span-2 flex md:flex-col items-stretch gap-2 md:items-end">
                  <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded border ${tint} text-center font-medium`}>
                    {a.status.replace("_", " ")}
                  </span>
                  <select
                    value={a.status}
                    onChange={(e) => setStatus(a.id, e.target.value)}
                    data-testid={`appointment-status-${a.id}`}
                    className="text-xs bg-black/30 border border-white/10 hover:border-white/25 text-white/80 rounded px-2 py-1 outline-none focus:border-[#FFB000]/50 transition-colors"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
